import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { advancedAPI, authAPI, membersAPI, unwrapList } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useLanguage } from '../../context/LanguageContext';
import './Pos.css';

const tabKeys = [
  { to: '/admin/advanced/staff', label: 'adv.staff' },
  { to: '/admin/advanced/members', label: 'adv.members' },
  { to: '/admin/advanced/audit', label: 'adv.audit' },
  { to: '/admin/two-factor', label: 'adv.totp' },
];

const roles = [
  { value: 'admin', label: 'role.admin' },
  { value: 'staff', label: 'role.staff' },
  { value: 'volunteer', label: 'role.volunteerName' },
];

const errorText = (err, fallback) => {
  const data = err.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.password) return Array.isArray(data.password) ? data.password[0] : data.password;
  if (data.phone_number) return Array.isArray(data.phone_number) ? data.phone_number[0] : data.phone_number;
  if (data.desk_role) return data.desk_role;
  return fallback;
};

const AdvancedSettings = () => {
  const { t } = useLanguage();
  return (
  <div className="admin-page">
    <h1>{t('adv.title')}</h1>
    <nav className="content-tabs" aria-label={t('adv.title')}>
      {tabKeys.map((tab) => (
        <NavLink key={tab.to} to={tab.to} className={({ isActive }) => (isActive ? 'active' : undefined)}>
          {t(tab.label)}
        </NavLink>
      ))}
    </nav>
    <Outlet />
  </div>
  );
};

export const StaffAccounts = () => {
  const { success, error } = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();
  const admin = user?.desk_role === 'admin';
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    phone_number: '',
    password: '',
    email: '',
    desk_role: 'staff',
    is_active: true,
  });
  const [passwords, setPasswords] = useState({});

  const load = () => advancedAPI.staff()
    .then((response) => setRows(unwrapList(response.data)))
    .catch((err) => error(errorText(err, '無法載入職員帳號')));

  useEffect(() => {
    load();
  }, []);

  const create = async (event) => {
    event.preventDefault();
    try {
      await advancedAPI.createStaff(form);
      success('職員帳號已建立');
      setForm({ phone_number: '', password: '', email: '', desk_role: 'staff', is_active: true });
      load();
    } catch (err) {
      error(errorText(err, '建立失敗'));
    }
  };

  const saveRow = async (row) => {
    try {
      await advancedAPI.updateStaff(row.id, {
        email: row.email || '',
        desk_role: row.desk_role,
        is_active: row.is_active,
      });
      success('已儲存');
      load();
    } catch (err) {
      error(errorText(err, '儲存失敗'));
    }
  };

  const resetPassword = async (id) => {
    const password = passwords[id] || '';
    try {
      await advancedAPI.resetPassword(id, password);
      success('密碼已重設');
      setPasswords((current) => ({ ...current, [id]: '' }));
    } catch (err) {
      error(errorText(err, '重設密碼失敗'));
    }
  };

  const patchRow = (id, field, value) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  return (
    <div className="pos-card">
      <h2>{t('adv.staff')}</h2>
      {admin && (
      <form className="pos-form-grid" onSubmit={create}>
        <label>{t('adv.phone')}
          <input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} required />
        </label>
        <label>{t('adv.password')}
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="new-password" />
        </label>
        <label>{t('adv.email')}
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label>{t('adv.role')}
          <select value={form.desk_role} onChange={(e) => setForm({ ...form, desk_role: e.target.value })}>
            {roles.map((role) => <option key={role.value} value={role.value}>{t(role.label)}</option>)}
          </select>
        </label>
        <div>
          <label className="pos-check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('adv.active')}
          </label>
          <p className="pos-check-note">{t('adv.activeNote')}</p>
        </div>
        <div className="pos-actions">
          <button type="submit" className="pos-btn">{t('adv.create')}</button>
        </div>
      </form>
      )}

      <ul className="pos-list">
        {rows.map((row) => (
          <li key={row.id}>
            <div>{row.phone_number}{row.email ? ` · ${row.email}` : ''}</div>
            <label>{t('adv.role')}
              <select value={row.desk_role} onChange={(e) => patchRow(row.id, 'desk_role', e.target.value)}>
                {roles.map((role) => <option key={role.value} value={role.value}>{t(role.label)}</option>)}
              </select>
            </label>
            <div>
              <label className="pos-check">
                <input type="checkbox" checked={row.is_active} onChange={(e) => patchRow(row.id, 'is_active', e.target.checked)} />
                {t('adv.active')}
              </label>
              <p className="pos-check-note">{t('adv.activeNote')}</p>
            </div>
            {admin && (
              <>
            <button type="button" className="pos-btn" onClick={() => saveRow(row)}>{t('adv.save')}</button>
            <label>{t('adv.resetPw')}
              <input type="password" value={passwords[row.id] || ''} autoComplete="new-password" onChange={(e) => setPasswords((current) => ({ ...current, [row.id]: e.target.value }))} />
            </label>
            <button type="button" className="pos-btn secondary" onClick={() => resetPassword(row.id)}>{t('adv.resetPw')}</button>
            <button type="button" className="pos-btn secondary" onClick={() => authAPI.totpReset(row.id).then(() => success(t('adv.resetTotp'))).catch(() => error(t('events.deleteRegFail')))}>{t('adv.resetTotp')}</button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const MemberAdvanced = () => {
  const { success, error } = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();
  const admin = user?.desk_role === 'admin';
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [member, setMember] = useState(null);
  const [idCard, setIdCard] = useState('');
  const [verified, setVerified] = useState(false);

  const openMember = (data) => {
    setMember(data);
    setIdCard(data.id_card_number || '');
    setVerified(Boolean(data.is_verified));
    setMatches([]);
  };

  const lookup = async () => {
    try {
      const found = await membersAPI.lookup(query.trim());
      const body = found.data;
      if (body.match === 'one' && body.results?.[0]) {
        openMember(body.results[0]);
        return;
      }
      setMember(null);
      setMatches(body.results || []);
      if (body.match === 'none') error('找不到會員');
    } catch (err) {
      error(errorText(err, '查找失敗'));
    }
  };

  const save = async () => {
    try {
      const response = await membersAPI.patch(member.id, {
        is_verified: verified,
      });
      openMember(response.data);
      success('會員進階資料已儲存');
    } catch (err) {
      error(errorText(err, '儲存失敗'));
    }
  };

  const regenerate = async () => {
    if (!window.confirm('確認重發 QR 令牌？舊碼將不能再用。')) return;
    try {
      const response = await membersAPI.regenerateQr(member.id);
      openMember(response.data);
      success('QR 令牌已更新');
    } catch (err) {
      error(errorText(err, '更新失敗'));
    }
  };

  return (
    <div className="pos-card">
      <h2>{t('adv.members')}</h2>
      <input className="pos-scan-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="會員編號、電話或姓名" aria-label="查找會員" />
      <div className="pos-actions">
        <button type="button" className="pos-btn" onClick={lookup}>查找</button>
      </div>
      {matches.length > 1 && (
        <ul className="pos-list">
          {matches.map((row) => (
            <li key={row.id}>
              <button type="button" className="pos-btn secondary" onClick={() => openMember(row)}>
                {row.chinese_name} · {row.member_number}
              </button>
            </li>
          ))}
        </ul>
      )}
      {member && (
        <div className="pos-section">
          <p><Link to={`/admin/members/${member.id}`}>{member.chinese_name} · {member.member_number}</Link></p>
          <label className="pos-check">
            <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
            已驗證
          </label>
          <div>QR 令牌：{member.qr_code_token || '—'}</div>
          {member.qr_code_token && (
            <QRCodeSVG value={member.qr_code_token} size={140} level="H" includeMargin />
          )}
          <div className="pos-actions">
            <button type="button" className="pos-btn" onClick={save}>儲存</button>
            {admin && (
              <button type="button" className="pos-btn secondary" onClick={regenerate}>{t('adv.reissue')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const AuditLogPage = () => {
  const { error } = useToast();
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [filters, setFilters] = useState({ action: '', date_from: '', date_to: '' });

  const load = (nextPage, nextFilters) => {
    const params = { page: nextPage };
    if (nextFilters.action) params.action = nextFilters.action;
    if (nextFilters.date_from) params.date_from = nextFilters.date_from;
    if (nextFilters.date_to) params.date_to = nextFilters.date_to;
    advancedAPI.audit(params)
      .then((response) => {
        setRows(unwrapList(response.data));
        setHasNext(Boolean(response.data?.next));
        setPage(nextPage);
      })
      .catch((err) => error(errorText(err, '無法載入操作紀錄')));
  };

  useEffect(() => {
    load(1, filters);
  }, []);

  return (
    <div className="pos-card">
      <h2>{t('adv.audit')}</h2>
      <div className="pos-form-grid">
        <label>動作
          <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
            <option value="">全部</option>
            <option value="register">報名</option>
            <option value="check_in">簽到</option>
            <option value="gift_redeem">領取禮品</option>
            <option value="note_edit">修改備註</option>
            <option value="role_change">更改角色</option>
            <option value="revoke">撤銷</option>
            <option value="member_edit">修改會員進階</option>
            <option value="password_reset">重設密碼</option>
            <option value="member_create">新增會員</option>
            <option value="contact_export">匯出通訊錄</option>
          </select>
        </label>
        <label>由
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        </label>
        <label>至
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        </label>
        <div className="pos-actions">
          <button type="button" className="pos-btn" onClick={() => load(1, filters)}>篩選</button>
        </div>
      </div>
      <ul className="pos-list">
        {rows.length === 0 && <li>暫無紀錄</li>}
        {rows.map((row) => (
          <li key={row.id}>
            <div>{row.created_at} · {row.action_label}</div>
            <div>{row.actor_phone || '—'} · {row.member_name || row.target_type} {row.target_id ? `#${row.target_id}` : ''}</div>
          </li>
        ))}
      </ul>
      <div className="pos-actions">
        <button type="button" className="pos-btn secondary" disabled={page <= 1} onClick={() => load(page - 1, filters)}>上一頁</button>
        <button type="button" className="pos-btn secondary" disabled={!hasNext} onClick={() => load(page + 1, filters)}>下一頁</button>
      </div>
    </div>
  );
};

export default AdvancedSettings;
